import * as React from 'react';
import { useMemo } from 'react';
import { useObservable } from 'react-use-observable';

import { Col, Row } from 'antd';

import { Field, useFormikContext } from 'formik';
import { SelectFormField } from 'v2/components/Shared/FormFields/SelectFormField';
import { TextFormField } from 'v2/components/Shared/FormFields/TextFormField';
import { CurrencyId } from 'v2/interfaces/financial';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import financialService from 'v2/services/financial';

import { accountNatureTypes, accountTypes, BANCO_DO_BRASIL_ID, CAIXA_ECONOMICA_ID, currencyTypes } from '../form';
import type { NewBankAccount } from '../form';
import { Layout } from '../Layout';

function BankData() {
  const [user] = useObservable(() => eduzzAccountsService.getUser(), []);
  const { values } = useFormikContext<NewBankAccount>();
  const isCrossborder = user?.international_account || user?.crossborder;
  const [banks, banksError] = useObservable(() => financialService.listBanks(isCrossborder).pipe(), [isCrossborder]);
  const [operations, operationsError] = useObservable(() => financialService.listOperations().pipe(), []);
  const isLoadingBanks = banks === null && banksError === null;
  const isLoadingOperations = operations === null && operationsError === null;

  const currencyOptions = useMemo(
    () =>
      currencyTypes.map(option => ({
        ...option,
        disabled:
          (option.value === CurrencyId.USD && !isCrossborder) || (option.value === CurrencyId.BRL && isCrossborder)
      })),
    [isCrossborder]
  );

  const bankOptions = useMemo(() => banks?.map(bank => ({ label: bank.value, value: bank.id })) ?? [], [banks]);

  const operationOptions = useMemo(
    () =>
      operations?.map(operation => ({ label: `${operation.id} - ${operation.operation}`, value: operation.id })) ?? [],
    [operations]
  );

  if (!user) return null;

  return (
    <Layout
      title='Dados bancários'
      description={<strong>Só é possível cadastrar contas que estejam em nome do titular da conta Eduzz</strong>}
    >
      <Row gutter={[16, 16]} className='-mx-2 my-6'>
        <Col xs={24} sm={16}>
          <Field
            name='account_nature'
            label='Natureza da conta*'
            fullWidth
            options={accountNatureTypes}
            component={SelectFormField}
          />
        </Col>
        <Col xs={24} sm={8}>
          <Field name='currency' label='Moeda*' fullWidth options={currencyOptions} component={SelectFormField} />
        </Col>
        <Col span={24}>
          {!isCrossborder && (
            <Field
              name='document_number'
              label='Digite seu CPF ou CNPJ*'
              placeholder='Digite o número'
              fullWidth
              mask='document'
              component={TextFormField}
              inputProps={{
                maxLength: 25
              }}
            />
          )}
          {isCrossborder && (
            <Field
              name='document_number'
              label='FEI/EIN Number*'
              placeholder='FEI/EIN'
              fullWidth
              component={TextFormField}
              inputProps={{
                maxLength: 25
              }}
              inputMode='numeric'
            />
          )}
        </Col>
        <Col span={24}>
          <Field
            name='name'
            label='Seu nome completo'
            placeholder='Nome do correntista'
            helperText='Só é possível cadastrar contas que estejam em nome do titular da conta Eduzz'
            component={TextFormField}
            disabled
            fullWidth
          />
        </Col>
        <Col span={24}>
          <Field
            name='bank_id'
            label='Escolha seu banco*'
            fullWidth
            loading={isLoadingBanks}
            options={bankOptions}
            placeholder='Selecione um banco'
            component={SelectFormField}
          />
        </Col>
        {values.bank_id === CAIXA_ECONOMICA_ID && (
          <Col span={24}>
            <Field
              name='operation_id'
              label='Operações Caixa Econômica*'
              fullWidth
              loading={isLoadingOperations}
              options={operationOptions}
              placeholder='Selecione uma operação'
              component={SelectFormField}
            />
          </Col>
        )}
        <Col span={24}>
          <Field name='type' label='Tipo de conta*' fullWidth options={accountTypes} component={SelectFormField} />
        </Col>
        <Col xs={24} sm={!isCrossborder ? 16 : 24}>
          <Field
            name='agency'
            label={!isCrossborder ? 'Número da agência*' : 'ABA Routing Number*'}
            mask='numberandzero'
            placeholder='Digite o número de sua agência'
            fullWidth
            component={TextFormField}
            inputProps={{
              maxLength: 20
            }}
            inputMode='numeric'
          />
        </Col>
        {!isCrossborder && (
          <Col xs={24} sm={8}>
            <Field
              name='agency_digit'
              label='Dígito (opcional)'
              placeholder='Digite o dígito'
              mask='singledigit'
              fullWidth
              component={TextFormField}
              inputProps={{
                maxLength: 1
              }}
              type='number'
            />
          </Col>
        )}
        <Col xs={24} sm={!isCrossborder ? 16 : 24}>
          <Field
            name='account'
            label={!isCrossborder ? 'Número da conta*' : 'Account Number*'}
            placeholder='Digite o número de sua conta'
            mask='numberandzero'
            fullWidth
            component={TextFormField}
            inputProps={{
              maxLength: 20
            }}
            inputMode='numeric'
          />
        </Col>
        {!isCrossborder && (
          <Col xs={24} sm={8}>
            <Field
              name='account_digit'
              label='Dígito*'
              placeholder='Digite o dígito'
              mask={values.bank_id === BANCO_DO_BRASIL_ID ? 'bankaccountdigit' : 'numberandzero'}
              fullWidth
              component={TextFormField}
              inputProps={{
                maxLength: 1
              }}
              type={values.bank_id === BANCO_DO_BRASIL_ID ? 'text' : 'number'}
            />
          </Col>
        )}
        {isCrossborder && (
          <Col span={24}>
            <Field
              name='notes'
              label='Observações opcional'
              multiline
              fullWidth
              component={TextFormField}
              InputProps={{
                rows: 5
              }}
              inputProps={{
                maxLength: 255
              }}
            />
          </Col>
        )}
      </Row>
    </Layout>
  );
}

export { BankData };
